import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { PILLAR } from "contracts/enums/metrics";
import { METRIC_CATALOG } from "contracts/report/catalog";
import { CONTENT_FITNESS_DIMENSIONS } from "contracts/report/content-fitness/dimensions";
import { Show } from "solid-js";
import { SCAN_SECTION_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { createWebsiteStore } from "../website/store";
import { ContentFitnessProvenance } from "./ContentFitnessProvenance";
import { ScanRouteSurface } from "./ScanRouteChrome";
import { ScanRouteHeader } from "./ScanRouteHeader";
import { ScanShell } from "./ScanShell";
import { SearchCoverage, SignalGroup } from "./ScanSignalRows";
import { createCompletedRunForReport } from "./scanCoverage";

const CATALOG_BY_KEY = new Map(
  METRIC_CATALOG.map((metric) => [metric.key, metric]),
);
const CONTENT_FITNESS_METRICS = CONTENT_FITNESS_DIMENSIONS.flatMap(
  (dimension) => {
    const metric = CATALOG_BY_KEY.get(dimension.key);
    return metric ? [metric] : [];
  },
);
const AI_REFERENCE_METRICS = METRIC_CATALOG.filter(
  (metric) => metric.pillar === PILLAR.AI_REFERENCE,
);
const TECHNICAL_METRICS = METRIC_CATALOG.filter(
  (metric) => metric.pillar === PILLAR.PERFORMANCE,
);
export function ScanSignals(props: { websiteId: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);
  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.SIGNALS}>
      <ScanRouteSurface class="signal-scorecard-route">
        <ScanRouteHeader
          websiteId={props.websiteId}
          section="Scorecard"
          title="Scorecard"
          subtitle="Content Fitness first, with separate supporting diagnostics that do not affect the headline score."
        />
        <Show
          when={context.data()}
          keyed
          fallback={<div class="loading">Loading scorecard...</div>}
        >
          {(data) => <Scorecard data={data} websiteId={props.websiteId} />}
        </Show>
      </ScanRouteSurface>
    </ScanShell>
  );
}

function Scorecard(props: { data: OverviewResponse; websiteId: string }) {
  const completedRun = createCompletedRunForReport(
    props.websiteId,
    () => props.data,
  );
  const byKey = new Map(
    props.data.signals.map((signal) => [signal.key, signal]),
  );
  return (
    <div class="sig-groups">
      <section class="sig-coverage-note">
        <h3>Content Fitness score provenance</h3>
        <ContentFitnessProvenance
          data={props.data}
          completedRun={completedRun()}
        />
      </section>
      <SignalGroup
        websiteId={props.websiteId}
        title="Content Fitness"
        kicker="Headline scoring"
        description="The five canonical weighted dimensions below are the only measures that contribute to the website Content Fitness score."
        metrics={CONTENT_FITNESS_METRICS}
        byKey={byKey}
        primary
      />

      <section class="sig-supporting">
        <div class="sig-supporting-head">
          <span>Supporting diagnostics</span>
          <h2>Additional context, kept separate from Content Fitness</h2>
          <p>
            These measures help explain discoverability and technical delivery.
            They are not extra Content Fitness dimensions and do not change the
            headline score.
          </p>
        </div>
        <SignalGroup
          websiteId={props.websiteId}
          title="AI Reference"
          kicker="Owned-site AI discoverability"
          description="Readiness inputs for AI access, source control, prominence and accuracy. External citations and competitor outcomes are not measured."
          metrics={AI_REFERENCE_METRICS}
          byKey={byKey}
          href={`/w/${props.websiteId}/scan/ai-reference`}
        />
        <SignalGroup
          websiteId={props.websiteId}
          title="Search & technical diagnostics"
          kicker="Technical delivery"
          description="Core Web Vitals cover loading performance, interaction responsiveness and visual stability. No combined Search score is calculated."
          metrics={TECHNICAL_METRICS}
          byKey={byKey}
        />
        <SearchCoverage byKey={byKey} />
      </section>
    </div>
  );
}
